import { EditorState, Plugin, PluginKey, Transaction } from 'prosemirror-state';

/**
 * Transaction meta key: set (to the new equation's position in the tagging transaction's resulting
 * document) by insertOrEditEquation's insert branch when it creates a brand-new empty equation, so
 * equationInsertPlugin can tell that insert apart from the same node arriving via collab.
 */
export const EquationInsertMetaKey = 'equation-insert';
export const EquationInsertPluginKey = new PluginKey<number | null>('equation-insert');

/**
 * Tracks the position of an equation node this LOCAL client just inserted (still empty, not yet
 * entered edit mode), so EquationNodeView can tell "I was the one who inserted this" apart from
 * "collab just delivered someone else's brand-new empty equation to me." Without this
 * distinction, every collaborator's own copy of the node view for a newly-inserted equation
 * would independently auto-enter edit mode — syncNode()'s hasRendered guard only protects
 * against re-opening an already-rendered equation, not against a *different* user's fresh
 * insert — yanking their cursor/focus to a node they never asked to edit (Bug: inserting an
 * equation and clicking away pulled other collaborators' focus to it).
 *
 * State is just the tracked position (or null), mapped forward through each transaction like any
 * other stored position. Plugin state for a client that never performed the insert stays null
 * forever, so the check below is always false for every collaborator except the one who inserted.
 */
export function equationInsertPlugin(): Plugin<number | null> {
  return new Plugin<number | null>({
    key: EquationInsertPluginKey,

    state: {
      init: () => null,
      apply(tr: Transaction, pos: number | null): number | null {
        const insertedAt = tr.getMeta(EquationInsertMetaKey);
        if (typeof insertedAt === 'number') {
          // An equation was just inserted so store its position in the plugin state
          return insertedAt;
        }

        return pos != null ? tr.mapping.map(pos) : null;
      }
    }
  });
}

/** Whether `pos` is the equation this local client just inserted (per equationInsertPlugin's tracked state). */
export function isLocalEquationInsert(state: EditorState, pos: number): boolean {
  return EquationInsertPluginKey.getState(state) === pos;
}
